import { SideNavBar } from '@aecom-ai-engineering/react-components';
import { logout } from '@consigli/oauth';
import clsx from 'clsx';
import {
  BookOpenText,
  CircleUser,
  LayoutDashboard,
  LogOut,
  type LucideIcon,
  Sparkles,
  Sticker,
} from 'lucide-react';
import { type FC, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useMatch } from 'react-router';

import { FeedbackDialog } from '@/components/feedback-dialog';
import { Route } from '@/routes';
import { authConfig } from '@/util/oauth/config';
import { prefersReducedMotion } from '@/util/prefers-reduced-motion';
import { TRAINING_URL } from '@/util/support-links';

import { LanguageSelect } from './language-select';
import { useSideNavLogoNavigation } from './use-side-nav-logo-navigation';

/** Must agree with SideNavBar.css, which owns the nav's own width. */
const SIDE_NAV_WIDTH_EXPANDED = 'var(--ds-space-60)';
const SIDE_NAV_WIDTH_COLLAPSED = 'var(--ds-space-14)';

/**
 * Fitted to the design-lab prototype's spring — re-fit against it rather than
 * substituting a stock easing. Spelled out, not interpolated: Tailwind reads
 * class names out of this file as text, so `duration-[${ms}ms]` emits no CSS.
 */
const WIDTH_TRANSITION =
  'transition-[width] duration-[300ms] ease-[cubic-bezier(0.3,0.45,0.4,1)] will-change-[width] motion-reduce:transition-none motion-reduce:will-change-auto';

/** Must agree with `duration-[120ms]` below, which the dip test pins. */
const DIP_OUT_MS = 120;

/** 120 out + 180 in = the 300ms width, so the nav settles with its own edge. */
const DIP_OUT = 'opacity-0 duration-[120ms] ease-in';
const DIP_IN = 'opacity-100 duration-[180ms] ease-out';

interface SideNavContentProps {
  icon: LucideIcon;
  label: string;
}

/** The icon and label every row shares, whatever element wraps them. */
const SideNavContent: FC<SideNavContentProps> = ({ icon: Icon, label }) => (
  <>
    <span aria-hidden="true" data-ds-side-nav-link-icon="">
      <Icon size={16} strokeWidth={1.75} />
    </span>
    <span data-ds-side-nav-link-label="">{label}</span>
  </>
);

interface SideNavItemProps extends SideNavContentProps {
  to: string;
}

/** One row of the navigation list. Module scope, so items are not remounted. */
const SideNavItem: FC<SideNavItemProps> = ({ icon, label, to }) => {
  const isActive = useMatch(to) !== null;

  return (
    <li data-ds-side-nav-item="">
      {/* data-active is styling only; aria-current is what a screen reader reads. */}
      <Link
        aria-current={isActive ? 'page' : undefined}
        data-active={isActive ? 'true' : undefined}
        data-ds-side-nav-link=""
        to={to}
      >
        <SideNavContent icon={icon} label={label} />
      </Link>
    </li>
  );
};

interface SideNavExternalLinkProps extends SideNavContentProps {
  href: string;
}

/** A footer row pointing off the app, in a new tab. An absolute URL never satisfies `useMatch`. */
const SideNavExternalLink: FC<SideNavExternalLinkProps> = ({ href, icon, label }) => (
  <a data-ds-side-nav-link="" href={href} rel="noopener noreferrer" target="_blank">
    <SideNavContent icon={icon} label={label} />
  </a>
);

interface SideNavButtonProps extends SideNavContentProps {
  onClick: () => void;
}

/** A row that acts rather than navigates — feedback, and logging out. */
const SideNavButton: FC<SideNavButtonProps> = ({ icon, label, onClick }) => (
  <button data-ds-side-nav-link="" onClick={onClick} type="button">
    <SideNavContent icon={icon} label={label} />
  </button>
);

interface AppSideNavProps {
  /** Owned by AppShell: TopNavBar toggles it, this consumes it. */
  collapsed: boolean;
}

/** Shared rather than page-local: its contents are app-level. */
export const AppSideNav: FC<AppSideNavProps> = ({ collapsed }) => {
  const { t } = useTranslation();
  const handleLogoClick = useSideNavLogoNavigation(`/${Route.PROJECTS}`);

  // Owned here rather than by the page: nothing else opens it, and the trigger
  // lives in the footer.
  const [isFeedbackOpen, setFeedbackOpen] = useState(false);

  // What the nav is wearing, as against `collapsed`, which is the target the
  // wrapper's width animates towards from t=0.
  const [navCollapsed, setNavCollapsed] = useState(collapsed);

  // Derived, not stored: toggling back mid-dip clears it rather than leaving the
  // nav stranded at opacity 0.
  const dipped = collapsed !== navCollapsed;

  // Layout, not passive: the reduced-motion branch must commit before paint, or
  // a frame shows the outgoing layout at the new width.
  useLayoutEffect(() => {
    if (!dipped) return;

    if (prefersReducedMotion()) {
      setNavCollapsed(collapsed);
      return;
    }

    const timer = window.setTimeout(() => setNavCollapsed(collapsed), DIP_OUT_MS);

    return () => window.clearTimeout(timer);
  }, [collapsed, dipped]);

  return (
    <>
      {/* Sticky and viewport-height: SideNavBar.css is min-height: 100% with a
       margin-top: auto footer, so left to grow it pushes language and log out
       below the fold. Scrolling the main column instead breaks paging —
       useInfiniteScroll leaves the observer root as the viewport.

       The width animates here, not on the nav, which inflates the collapsed
       AECOM mark. The interior cannot animate at all: every collapsed rule in
       SideNavBar.css keys off [data-collapsed], a single-frame flip — hence the
       dip. The fill and border live here too, with the nav's right border
       transparent, not border-r-0, so the aside stays 240px border-box.

       SideNavBar should own all of this upstream — both consumers have now
       built it separately — and that needs the logo asset to carry an intrinsic
       size first. SideNavBar.css:85-91 sizes the lockup from the column, so the
       box cannot be animated without the logo growing with it; the collapsed
       mark is already pinned explicitly at :122-126 for exactly that reason, so
       the fix is to do at the expanded state what the DS already did at the
       collapsed one. The dip would still be ours: promoting the width alone
       does not make the interior animate. Written down here because this is the
       only place the two-consumer evidence lives; it was lost in the file move
       that created this directory. */}
      <div
        className={clsx(
          'sticky top-0 h-screen shrink-0 self-start overflow-hidden',
          'border-r border-solid border-[var(--ds-color-border-primary)] bg-[var(--ds-color-bg-secondary)]',
          WIDTH_TRANSITION,
        )}
        style={{ width: collapsed ? SIDE_NAV_WIDTH_COLLAPSED : SIDE_NAV_WIDTH_EXPANDED }}
      >
        <SideNavBar
          ariaLabel={t('navbar.side-nav.label')}
          className={clsx(
            'h-full border-r-transparent transition-opacity motion-reduce:transition-none',
            dipped ? DIP_OUT : DIP_IN,
          )}
          collapsed={navCollapsed}
          logoHref={`/${Route.PROJECTS}`}
          onClick={handleLogoClick}
          menuItems={
            <>
              {/* navbar.menu.home, not navbar.breadcrumbs.home — that key is
                still "All projects" for the old page and project creation. */}
              <SideNavItem
                icon={LayoutDashboard}
                label={t('navbar.menu.home')}
                to={`/${Route.PROJECTS}`}
              />
              {/* Global, not the project workspace's Agents tab: this is the
                catalogue of what the product offers, reachable without a
                project. */}
              <SideNavItem
                icon={Sparkles}
                label={t('navbar.menu.agents')}
                to={`/${Route.AGENTS}`}
              />
              <SideNavItem icon={CircleUser} label={t('navbar.menu.myprofile')} to="/profile" />
            </>
          }
          footer={
            // Two children 8px apart: the select, and a gapless group of rows.
            // [data-ds-side-nav-bar-footer] supplies no gap of its own.
            <div className="flex flex-col gap-2">
              {/* Full width: the 99px in the Figma frame was an in-progress edit,
                not the intent — at 99px "English" truncated. Shown when collapsed
                as of design-styles 0.20.0. */}
              <LanguageSelect />
              {/* Gapless: the design groups these like the menu list. */}
              <div className="flex flex-col">
                <SideNavButton
                  icon={Sticker}
                  label={t('navbar.menu.feedback')}
                  onClick={() => setFeedbackOpen(true)}
                />
                <SideNavExternalLink
                  href={TRAINING_URL}
                  icon={BookOpenText}
                  label={t('navbar.menu.training')}
                />
                <SideNavButton
                  icon={LogOut}
                  label={t('navbar.menu.logout')}
                  onClick={() => logout(authConfig)}
                />
              </div>
            </div>
          }
        />
      </div>

      {/* Outside the sticky wrapper: sticky makes a stacking context, so the
          cards would paint over the dialog. */}
      <FeedbackDialog open={isFeedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </>
  );
};
